/* eslint-disable promise/prefer-await-to-callbacks */
import { logWarning } from 'shared-code/log'
import { AuthContext } from '../contexts/AuthContext'
import { useEffect, useState } from 'react'
import { useCdpClient } from './use-global-cdp-client'

interface UseClearbuyCookiesOpts {
  clearbuyCookieUrl?: string
}

export function useClearbuyCookies(opts: UseClearbuyCookiesOpts): void {
  const { authState, onAuthEvent } = AuthContext.useContainer()
  const cdpClient = useCdpClient()

  const [sessionId, setSessionId] = useState<string | null>(null)
  const userId = authState?.profile?.sub ?? null

  useEffect(() => {
    setSessionId(cdpClient?.getSessionId() ?? null)
  }, [cdpClient])

  useEffect(() => {
    const removeUserId = (): void => {
      if (!opts.clearbuyCookieUrl) {
        logWarning('Clearbuy cookies: clearbuyCookieUrl is undefined')
        return
      }

      setCookies(opts.clearbuyCookieUrl, {
        userId: null,
      })
    }

    const cleanup = onAuthEvent('logout', removeUserId)
    const cleanup2 = onAuthEvent('check-session-failed', removeUserId)

    return () => {
      cleanup()
      cleanup2()
    }
  }, [])

  useEffect(() => {
    if (!opts.clearbuyCookieUrl) {
      logWarning('Clearbuy cookies: clearbuyCookieUrl is undefined')
      return
    }

    setCookies(opts.clearbuyCookieUrl, {
      userId: userId ?? undefined,
      sessionId: sessionId ?? undefined,
    })
  }, [sessionId, userId])
}

interface SetCookiesOpts {
  sessionId?: string | null // null means remove cookie, undefined means do not set cookie
  userId?: string | null // null means remove cookie, undefined means do not set cookie
}

let timeout: ReturnType<typeof setTimeout> | null = null
let pendingOpts: SetCookiesOpts = {}

function setCookies(urlString: string, opts: SetCookiesOpts): void {
  for (const key in opts) {
    const k = key as keyof SetCookiesOpts
    pendingOpts[k] = opts[k]
  }

  if (timeout) {
    clearTimeout(timeout)
    timeout = null
  }

  timeout = setTimeout(() => {
    const url = new URL(urlString)
    if (pendingOpts.sessionId === undefined && pendingOpts.userId === undefined) {
      return
    }

    void fetch(url, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify(pendingOpts),
    }).catch(error => {
      logWarning('Clearbuy cookies error', error)
    })

    pendingOpts = {}
  }, 1000)
}
